/**
 * EmailComposeArtifact — side-panel email compose surface
 *
 * Renders an email draft (typically emitted by the AI as a structured artifact)
 * with visible To/CC/BCC chips, editable subject, and preview/edit toggle on body.
 *
 * Expected artifact shape:
 *   {
 *     type: 'email_compose',
 *     from: 'waseem.malik@amtrend.com',
 *     to: ['shirley.morales@amtrend.com'],
 *     cc: ['uzziel.bucio@amtrend.com'],
 *     bcc: [],
 *     subject: 'Subject line',
 *     body_html: '<p>...</p>',   // OR body: 'plain text' (will be wrapped)
 *     status: 'draft' | 'sending' | 'sent' | 'error',
 *     error?: string,
 *   }
 *
 * Action props:
 *   onSend(payload) — called when user clicks Send (after they've confirmed)
 *   onDiscard()     — called when user clicks Discard
 */

import { useState, useMemo, useRef, useEffect } from 'react'
import { Mail, Send, Trash2, Edit3, Eye, X, AlertCircle, CheckCircle2, Loader2, User } from 'lucide-react'
import DOMPurify from 'dompurify'
import './EmailComposeArtifact.css'

// --- Editor command helpers (scoped to this component) ---

// True if the cursor sits at offset 0 of the first text-content of `li`.
function isAtStartOfLi(li, range) {
  if (!range || !range.collapsed) return false
  const pre = document.createRange()
  pre.selectNodeContents(li)
  try {
    pre.setEnd(range.startContainer, range.startOffset)
  } catch {
    return false
  }
  return pre.toString().length === 0
}

// True if the li has no visible text content.
function isLiEmpty(li) {
  return (li.textContent || '').replace(/ /g, '').trim() === ''
}

// Exit the list: convert this `<li>` into a `<p>` placed after the list,
// then place the cursor inside the new paragraph.
function exitListToParagraph(li) {
  const list = li.closest('ol, ul')
  if (!list || !list.parentNode) return false
  const p = document.createElement('p')
  // Preserve any content already in the li (Backspace case carries text out)
  if (!isLiEmpty(li)) {
    while (li.firstChild) p.appendChild(li.firstChild)
  } else {
    p.appendChild(document.createElement('br'))
  }
  list.parentNode.insertBefore(p, list.nextSibling)
  li.remove()
  if (list.children.length === 0) list.remove()
  // Move cursor to start of the new paragraph
  const sel = window.getSelection()
  sel.removeAllRanges()
  const r = document.createRange()
  r.setStart(p, 0)
  r.collapse(true)
  sel.addRange(r)
  return true
}

// Allowed tags/attributes when pasting — keep structure + emphasis, strip colors/fonts.
const PASTE_ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a',
  'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
  'h1', 'h2', 'h3', 'span', 'div',
]
const PASTE_ALLOWED_ATTR = ['href', 'target', 'rel']

function sanitizePastedHtml(html) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: PASTE_ALLOWED_TAGS,
    ALLOWED_ATTR: PASTE_ALLOWED_ATTR,
    // strip any style attribute or font tag content
    FORBID_ATTR: ['style', 'color', 'face', 'size', 'class', 'id'],
    FORBID_TAGS: ['font', 'style', 'script'],
  })
}

// Render-time sanitizer for body_html the AI emitted. The AI is trusted to follow
// the prompt's tag allowlist, but defense-in-depth: strip <script>, event handlers,
// and dangerous attributes that could enable XSS if the prompt were ever subverted.
function sanitizeBodyForRender(html) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: PASTE_ALLOWED_TAGS,
    ALLOWED_ATTR: PASTE_ALLOWED_ATTR,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
    // All on* event handler attributes are stripped by default by DOMPurify.
  })
}

function displayNameFromEmail(email) {
  if (!email || typeof email !== 'string') return ''
  const local = email.split('@')[0] || email
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function RecipientChip({ email, onRemove, readOnly }) {
  const name = displayNameFromEmail(email)
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-100 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/50"
      title={email}
    >
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-semibold">
        {(name[0] || email[0] || '?').toUpperCase()}
      </span>
      <span className="max-w-[12rem] truncate">{name || email}</span>
      <span className="text-blue-500/70 dark:text-blue-400/70 text-[10px] hidden sm:inline">
        &lt;{email}&gt;
      </span>
      {!readOnly && (
        <button
          type="button"
          onClick={() => onRemove?.(email)}
          className="ml-0.5 text-blue-500 hover:text-blue-800 dark:hover:text-blue-100"
          aria-label={`Remove ${email}`}
        >
          <X size={12} />
        </button>
      )}
    </span>
  )
}

function RecipientRow({ label, emails, onRemove, onAdd, readOnly, autoFocus }) {
  const [draft, setDraft] = useState('')

  const commitDraft = () => {
    const candidate = draft.trim().replace(/[,;\s]+$/, '')
    if (!candidate) return
    if (!candidate.includes('@')) return
    onAdd?.(candidate)
    setDraft('')
  }

  return (
    <div className="flex items-start gap-3 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
      <div className="w-12 flex-shrink-0 pt-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </span>
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-1.5">
        {emails.map(email => (
          <RecipientChip key={email} email={email} onRemove={onRemove} readOnly={readOnly} />
        ))}
        {!readOnly && (
          <input
            type="email"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                e.preventDefault()
                commitDraft()
              } else if (e.key === 'Backspace' && !draft && emails.length > 0) {
                onRemove?.(emails[emails.length - 1])
              }
            }}
            placeholder={emails.length === 0 ? `Add ${label.toLowerCase()}...` : ''}
            autoFocus={autoFocus}
            className="flex-1 min-w-[8rem] bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none py-1"
          />
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status, error }) {
  if (status === 'sending') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
        <Loader2 size={12} className="animate-spin" />
        Sending...
      </span>
    )
  }
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
        <CheckCircle2 size={12} />
        Sent
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
        title={error}
      >
        <AlertCircle size={12} />
        Failed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
      Draft
    </span>
  )
}

export default function EmailComposeArtifact({ data, onSend, onDiscard, onAutoClose, autoCloseMs = 1500 }) {
  const initialBody = useMemo(() => {
    if (data?.body_html) return data.body_html
    if (data?.body) {
      const escaped = String(data.body)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return escaped.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('')
    }
    return '<p></p>'
  }, [data?.body_html, data?.body])

  const [from] = useState(data?.from || '')
  const [to, setTo] = useState(data?.to || [])
  const [cc, setCc] = useState(data?.cc || [])
  const [bcc, setBcc] = useState(data?.bcc || [])
  const [showBcc, setShowBcc] = useState((data?.bcc || []).length > 0)
  const [subject, setSubject] = useState(data?.subject || '')
  const [bodyHtml, setBodyHtml] = useState(initialBody)
  // Memoize the sanitized version so we don't re-run DOMPurify on every render.
  // Only the preview path uses this; edit mode owns the DOM directly.
  const sanitizedBodyHtml = useMemo(() => sanitizeBodyForRender(bodyHtml), [bodyHtml])
  const [mode, setMode] = useState('preview') // 'preview' | 'edit'
  const [status, setStatus] = useState(data?.status || 'draft')
  const editorRef = useRef(null)
  const autoCloseTimerRef = useRef(null)
  const [hovered, setHovered] = useState(false)
  // Track whether the local Send action drove us into 'sent' (vs. mounting with status='sent' from props,
  // which happens when re-viewing a previously-sent email — we should NOT auto-close in that case).
  const [justSentLocally, setJustSentLocally] = useState(false)

  // When entering edit mode, seed the contenteditable from current bodyHtml.
  // Uncontrolled from here — browser owns the DOM until blur/Send.
  useEffect(() => {
    if (mode === 'edit' && editorRef.current) {
      editorRef.current.innerHTML = bodyHtml
    }
  }, [mode]) // intentionally not depending on bodyHtml — we don't want to fight the editor mid-typing

  // Auto-close after Send succeeds. Only fires when the user clicked Send in *this* session
  // (not when remounting with status='sent' to review a previously-sent email). Pauses while hovered.
  useEffect(() => {
    if (!justSentLocally || !onAutoClose) return
    if (hovered) return // paused — will re-arm when hover ends
    autoCloseTimerRef.current = setTimeout(() => {
      onAutoClose({ from, to, cc, bcc, subject, body_html: bodyHtml, sent_at: new Date().toISOString() })
    }, autoCloseMs)
    return () => {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current)
    }
  }, [justSentLocally, hovered, onAutoClose, autoCloseMs])

  const removeFrom = (list, setter) => email => setter(list.filter(e => e !== email))
  const addTo = (list, setter) => email => {
    if (!list.includes(email)) setter([...list, email])
  }

  const isSent = status === 'sent'
  const isSending = status === 'sending'
  const isLocked = isSent || isSending
  const canSend = !isLocked && to.length > 0 && subject.trim().length > 0

  const handleSend = async () => {
    if (!canSend) return
    setStatus('sending')
    const payload = { from, to, cc, bcc, subject, body_html: bodyHtml }
    try {
      if (onSend) {
        await onSend(payload)
      } else {
        // Prototype default — log and pretend.
        console.log('[EmailComposeArtifact] Send (no handler):', payload)
        await new Promise(r => setTimeout(r, 600))
      }
      setStatus('sent')
      setJustSentLocally(true)
    } catch (err) {
      console.error('[EmailComposeArtifact] Send failed:', err)
      setStatus('error')
    }
  }

  const handleDiscard = () => {
    if (isSending) return
    onDiscard?.()
  }

  return (
    <div
      className="h-full flex flex-col bg-gray-50 dark:bg-surface-dark"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header card — recipients + subject */}
      <div className="bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        {/* Title strip */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              New Email
            </span>
            <StatusBadge status={status} error={data?.error} />
          </div>
          <div className="flex items-center gap-1">
            {!showBcc && !isLocked && (
              <button
                type="button"
                onClick={() => setShowBcc(true)}
                className="text-xs text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 px-2 py-1 rounded"
              >
                + BCC
              </button>
            )}
          </div>
        </div>

        {/* From row (display-only) */}
        {from && (
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
            <div className="w-12 flex-shrink-0">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                From
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <User size={14} className="text-gray-400" />
              <span>{from}</span>
            </div>
          </div>
        )}

        <RecipientRow
          label="To"
          emails={to}
          onRemove={removeFrom(to, setTo)}
          onAdd={addTo(to, setTo)}
          readOnly={isLocked}
        />
        <RecipientRow
          label="CC"
          emails={cc}
          onRemove={removeFrom(cc, setCc)}
          onAdd={addTo(cc, setCc)}
          readOnly={isLocked}
        />
        {showBcc && (
          <RecipientRow
            label="BCC"
            emails={bcc}
            onRemove={removeFrom(bcc, setBcc)}
            onAdd={addTo(bcc, setBcc)}
            readOnly={isLocked}
          />
        )}

        {/* Subject row */}
        <div className="flex items-center gap-3 px-4 py-2.5">
          <div className="w-12 flex-shrink-0">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Subject
            </span>
          </div>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            readOnly={isLocked}
            placeholder="Add a subject"
            className="flex-1 bg-transparent text-sm font-medium text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none py-1"
          />
        </div>
      </div>

      {/* Body toolbar — preview/edit toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-surface-dark-raised border-b dark:border-gray-700">
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-surface-dark-overlay">
          <button
            type="button"
            onClick={() => setMode('preview')}
            disabled={isLocked && mode !== 'preview'}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${
              mode === 'preview'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <Eye size={12} />
            Preview
          </button>
          <button
            type="button"
            onClick={() => setMode('edit')}
            disabled={isLocked}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${
              mode === 'edit'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Edit3 size={12} />
            Edit
          </button>
        </div>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          {mode === 'edit' ? 'Click in the body to edit' : 'Rendered preview'}
        </span>
      </div>

      {/* Body content */}
      <div className="flex-1 overflow-auto bg-white dark:bg-surface-dark-raised">
        {mode === 'preview' ? (
          <div
            className="email-body-preview p-6 text-gray-800 dark:text-gray-200 leading-relaxed text-sm"
            // DOMPurified upstream via sanitizeBodyForRender — safe to inject.
            dangerouslySetInnerHTML={{ __html: sanitizedBodyHtml }}
          />
        ) : (
          <div
            ref={editorRef}
            contentEditable={!isLocked}
            suppressContentEditableWarning
            spellCheck
            onInput={e => setBodyHtml(e.currentTarget.innerHTML)}
            onBlur={e => setBodyHtml(e.currentTarget.innerHTML)}
            onKeyDown={e => {
              const sel = window.getSelection()
              if (!sel || sel.rangeCount === 0) return
              const range = sel.getRangeAt(0)
              // Find the containing <li>, if any
              let node = range.startContainer
              if (node.nodeType === 3) node = node.parentNode
              const li = node?.closest?.('li')
              if (!li) return

              // Enter on an empty list item → exit the list
              if (e.key === 'Enter' && !e.shiftKey && isLiEmpty(li)) {
                e.preventDefault()
                exitListToParagraph(li)
                // notify React of the new HTML
                setBodyHtml(e.currentTarget.innerHTML)
                return
              }
              // Backspace at the very start of a list item → exit the list
              if (e.key === 'Backspace' && isAtStartOfLi(li, range)) {
                e.preventDefault()
                exitListToParagraph(li)
                setBodyHtml(e.currentTarget.innerHTML)
                return
              }
            }}
            onPaste={e => {
              e.preventDefault()
              const cd = e.clipboardData
              const html = cd.getData('text/html')
              const text = cd.getData('text/plain')
              let toInsert
              if (html) {
                toInsert = sanitizePastedHtml(html)
              } else {
                // Plain-text paste — escape and convert linebreaks to <br>
                const esc = text
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                toInsert = esc.replace(/\n/g, '<br/>')
              }
              // Insert via range.insertNode — bypasses execCommand's style-normalization
              // (which would otherwise wrap inserted content with the surrounding font-size).
              const sel = window.getSelection()
              if (!sel || sel.rangeCount === 0) return
              const range = sel.getRangeAt(0)
              range.deleteContents()
              const tpl = document.createElement('template')
              tpl.innerHTML = toInsert
              const frag = tpl.content
              const lastNode = frag.lastChild
              range.insertNode(frag)
              if (lastNode) {
                range.setStartAfter(lastNode)
                range.collapse(true)
                sel.removeAllRanges()
                sel.addRange(range)
              }
              setBodyHtml(e.currentTarget.innerHTML)
            }}
            className="email-body-preview p-6 text-gray-800 dark:text-gray-200 leading-relaxed text-sm focus:outline-none ring-1 ring-inset ring-blue-200 dark:ring-blue-900/40 bg-blue-50/20 dark:bg-blue-950/10"
            style={{ minHeight: '300px' }}
          />
        )}
      </div>

      {/* Footer — actions */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-surface-dark-raised border-t dark:border-gray-700">
        <button
          type="button"
          onClick={handleDiscard}
          disabled={isSending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSent ? <X size={14} /> : <Trash2 size={14} />}
          {isSent ? 'Close' : 'Discard'}
        </button>

        {/* Auto-close hint when in the post-send window */}
        {justSentLocally && (
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            {hovered ? 'Hover holding open' : 'Closing in a moment…'}
          </span>
        )}

        <div className="flex items-center gap-2">
          {status === 'error' && data?.error && (
            <span className="text-xs text-red-600 dark:text-red-400 max-w-[16rem] truncate" title={data.error}>
              {data.error}
            </span>
          )}
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isSending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sending...
              </>
            ) : isSent ? (
              <>
                <CheckCircle2 size={14} />
                Sent
              </>
            ) : (
              <>
                <Send size={14} />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
